import {BaseError} from 'viem';
import {DEX_KNOWN_ERRORS_NAMING, DexErrorNamingRec} from './dex-tx-error-naming';
import {ParsedReverDataViem, parseRevertErrorViem} from './revert-error';

export class DexTxError extends Error {

    public readonly details: Readonly<ParsedReverDataViem>;

    public readonly naming?: Readonly<DexErrorNamingRec>;

    constructor(message: string, details: Readonly<ParsedReverDataViem>, options?: ErrorOptions) {
        super(message, options);
        this.name = 'DexTxError';
        this.message = message;
        this.details = details;
        this.naming = this.getNaming(details?.name, details?.selector);

        this.toConsole();
    }

    public static async fromViem(
        message: string,
        error: BaseError | unknown,
        fallback: boolean = true
    ): Promise<DexTxError | Error | unknown> {
        const parsed = await parseRevertErrorViem(error);

        return parsed
            ? new DexTxError(message, parsed, {cause: error})
            : fallback
                ? new Error(message, {cause: error})
                : undefined;
    }

    public override toString(): string {
        return `${this.name}: ${this.message}\n\n${this.formatDetailsForConsole()}`;
    }

    public toJSON(): any {
        return {
            name: this.name,
            message: this.message,
            stack: this.stack,
            details: this.details
        };
    }

    public toConsole(): void {
        console.groupCollapsed(
            `%c[${this.name}]%c ${this.message} ${this.details?.name}`,
            'color: #C74142;',
            ''
        );
        console.log(this.details);
        console.log(this.cause);
        console.groupEnd();
    }

    private formatDetailsForConsole(): string {
        return Object.entries(this.details)
            .map(([key, value]) => `  ${key}: ${this.formatValue(value)}`)
            .join('\n');
    }

    private formatValue(value: unknown): string {
        if (typeof value === 'bigint') {
            return value.toString() + 'n';
        }
        if (typeof value === 'string' && value.startsWith('0x')) {
            return value;
        }
        if (Array.isArray(value)) {
            return `[${value.map(v => this.formatValue(v)).join(', ')}]`;
        }
        return JSON.stringify(value);
    }

    private getNaming(errorName: string | undefined, selector: string | undefined): DexErrorNamingRec | undefined {
        if (!errorName) {
            return undefined;
        }

        const naming = DEX_KNOWN_ERRORS_NAMING.hasOwnProperty(errorName)
            ? DEX_KNOWN_ERRORS_NAMING[errorName as keyof typeof DEX_KNOWN_ERRORS_NAMING]
            : undefined;

        return naming && {
            key: errorName as keyof typeof DEX_KNOWN_ERRORS_NAMING,
            selector,
            ...naming
        } satisfies DexErrorNamingRec;
    }

}
